// src/pages/MyPage/CancelUserReason.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFixedLayout } from "../../../hooks/useFixedLayout";
import { HeaderIconL } from "../../../assets/components/HeaderGroup";
import left from "../../../assets/icons/Arrow/left.svg";
import axios from "axios";
import { postToApp } from "../../../bridges/appBridge";
import ClearTextArea from "../../CommonComponents/ClearTextArea";
import trash from "../../../assets/icons/System/delete.svg";
import CancelUserConfirmPopup from "./CancelComponents/CancelUserConfirmPopup";
import { SingleButton } from "../../../assets/components/ButtonGroup";
import SectionTitle from "../../../assets/components/TitleGroup";
import ListCard from "../../../assets/components/ListCard";

const REASONS = [
    { id: "not_often", label: "자주 사용하지 않아요" },
    { id: "not_satisfied", label: "원하는 이미지가 나오지 않아요" },
    { id: "no_store", label: "이제 가게를 운영하지 않아요" },
    { id: "error", label: "앱에 오류가 있어요" },   // 텍스트필드 필요
    { id: "custom", label: "직접 입력" },           // 텍스트필드 필요
];

// textarea placeholder
const getPlaceholderById = (id) => {
    switch (id) {
        case "error":
            return "(필수)발견하신 오류를 작성해 주세요";
        case "custom":
            return "(필수)탈퇴 사유를 작성해 주세요";
        default:
            return "";
    }
};


const CancelUserReason = () => {
    const navigate = useNavigate();
    const { headerRef, footerRef, headerSpace, bottomSpace } = useFixedLayout();

    const [selectedId, setSelectedId] = useState(null);
    const [reasonDetail, setReasonDetail] = useState(""); // ✅ 상세 사유는 하나로 관리
    const [isDeleting, setIsDeleting] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const userId = localStorage.getItem("user_id");

    const headerTitle = "탈퇴 사유";

    const needsText = selectedId === "error" || selectedId === "custom";
    const textValue = needsText ? reasonDetail : "";

    const canSubmit =
        !!selectedId && (!needsText || textValue.trim().length > 0);

    const handleBack = () => navigate(-1);

    // 세션/스토리지 초기화
    function resetClientSessionForRegister() {
        try {
            if (axios?.defaults?.headers?.common?.Authorization) {
                delete axios.defaults.headers.common.Authorization;
            }
        } catch { }

        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("user_id");
        localStorage.removeItem("store_business_number");

        sessionStorage.removeItem("kcb_starting");
        sessionStorage.removeItem("kcb_done");
        sessionStorage.removeItem("kcb_enc");
        sessionStorage.removeItem("naver_oauth_state");
        sessionStorage.removeItem("naver_redirect_uri");
    }

    // 회원탈퇴
    const handleDeleteUser = async () => {
        if (isDeleting) return;

        setIsDeleting(true);

        const selectedReason = REASONS.find((r) => r.id === selectedId) || null;
        const reasonLabel = selectedReason?.label || null;
        const reasonDetailForDB =
            needsText && reasonDetail.trim().length > 0
                ? reasonDetail.trim()
                : null;

        const payload = {
            user_id: userId,
            reason_id: selectedId,        // 예: "not_often"
            reason_label: reasonLabel,    // 예: "자주 사용하지 않아요"
            reason_detail: reasonDetailForDB, // 예: "버벅거림이 심해요" / 없으면 null
        };

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_FASTAPI_ADS_URL}/ads/user/delete`,
                payload,
                {
                    headers: { "Content-Type": "application/json" },
                }
            );

            const ok = response?.data?.success === true;

            if (ok) {
                try {
                    postToApp({ type: "START_SIGNOUT" });
                } catch { }

                resetClientSessionForRegister();

                // alert("탈퇴가 완료되었습니다. 모든 기기에서 로그아웃됩니다.");
                navigate("/cancel/complete", { replace: true });
            } else {
                const msg =
                    response?.data?.message ||
                    "탈퇴 처리에 실패했습니다. 잠시 후 다시 시도해주세요.";
                alert(msg);
            }
        } catch (error) {
            if (error?.response) {
                const apiMsg =
                    error.response?.data?.detail ||
                    error.response?.data?.message ||
                    "서버 오류가 발생했습니다.";
                alert(apiMsg + ` (코드: ${error.response.status})`);
            } else if (error?.request) {
                alert(
                    "네트워크 오류로 탈퇴 요청에 실패했습니다. 인터넷 연결을 확인한 후 다시 시도해주세요."
                );
            } else {
                alert("알 수 없는 오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
                console.error("탈퇴 처리 중 오류:", error);
            }
        } finally {
            setIsDeleting(false);
        }
    };

    // 사유 클릭 시
    const handleSelectReason = (reasonId, nextSelected) => {
        setSelectedId((prev) => {
            // ListCard가 토글을 주긴 하지만, 라디오처럼 동작하게 유지
            const nextId = nextSelected ? reasonId : null;

            // 선택 해제되면 텍스트도 비워주고,
            // error/custom 아닌 항목 선택 시에도 일단 초기화
            if (
                nextId === null ||
                (reasonId !== "error" && reasonId !== "custom")
            ) {
                setReasonDetail("");
            }

            return nextId;
        });
    };

    return (
        <div className="flex flex-col min-h-dvh bg-surface-default">
            {/* 헤더 */}
            <div ref={headerRef} className="fixed top-0 left-0 right-0 z-10">
                <HeaderIconL
                    className=""
                    title={headerTitle}
                    icon={left}
                    onIconClick={handleBack}
                />
            </div>

            <div
                className="flex-1 overflow-y-auto px-[16px]"
                style={{ paddingTop: headerSpace, paddingBottom: bottomSpace }}
            >
                <SectionTitle
                    className="pt-[16px]"
                    title="탈퇴 사유를 알려주세요"
                    subTitle={"더 좋은 서비스가 되기 위해 회원님의 의견을 참고할게요."}
                />

                {/* 사유 리스트 */}
                <section className="pt-[40px] pb-[16px] flex-1 space-y-[12px]">
                    {REASONS.map((reason) => {
                        const selected = selectedId === reason.id;
                        const showTextField =
                            selected &&
                            (reason.id === "error" || reason.id === "custom");

                        return (
                            <React.Fragment key={reason.id}>
                                {/* ListCard 활용한 라디오 스타일 카드 */}
                                <ListCard
                                    mode="checkable"
                                    title={reason.label}
                                    selected={selected}
                                    onSelect={(next) => handleSelectReason(reason.id, next)}
                                />

                                {/* 카드 아래 텍스트 필드 */}
                                {showTextField && (
                                    <div className="">
                                        <ClearTextArea
                                            maxLength={100}
                                            rows={5}
                                            placeholder={getPlaceholderById(reason.id)}
                                            value={reasonDetail}
                                            onChange={(e) => setReasonDetail(e.target.value)}
                                            showClearWhen={() => false}
                                        />

                                        {/* 전체 삭제 + 카운트 */}
                                        <div className="flex items-center justify-between pt-[8px]">
                                            <button
                                                type="button"
                                                className="inline-flex items-center gap-[2px] whitespace-nowrap"
                                                onClick={() => setReasonDetail("")}
                                                disabled={!reasonDetail}
                                            >
                                                <img
                                                    src={trash}
                                                    alt="삭제"
                                                    className="h-[20px] w-[20px]"
                                                />
                                                <span className="text-c2 text-text-danger leading-[20px]">
                                                    전체 삭제
                                                </span>
                                            </button>

                                            <div className="text-c2 text-text-subtler">
                                                {reasonDetail.length} / 100자 이내
                                            </div>
                                        </div>

                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}
                </section>
            </div>

            {/* 하단 제출 버튼 */}
            <section
                ref={footerRef}
                className="fixed bottom-0 left-0 right-0 bg-surface-default"
            >
                <SingleButton
                    onClick={() => {
                        if (!canSubmit || isDeleting) return;
                        setShowConfirm(true); // 팝업만 열기
                    }}
                    disabled={!canSubmit || isDeleting}
                >
                    {isDeleting ? "처리 중..." : "제출"}
                </SingleButton>
            </section>

            <CancelUserConfirmPopup
                open={showConfirm}
                onConfirm={() => {
                    setShowConfirm(false);
                    handleDeleteUser(); // 실제 탈퇴 API 호출
                }}
                onCancel={() => setShowConfirm(false)}
            />
        </div>
    );
};

export default CancelUserReason;
